import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UsersSettingConstant } from 'app/users-settings/users-settings.constant';
import { ToastrService } from 'ngx-toastr';
import { FrameworkEnumTypes } from '../enums/framework-enum-types.enum';
import { FileService } from '../file.service';
import { UserService } from '../user.service';
import { UtilsService } from '../utils.service';
import { GetEntityQuery, GetUserQuery, RoleEnum, StandardType } from 'app/API.service';
import { DomainFrameworkService } from '../domain-framework.service';
import { Router } from '@angular/router';
import { FrameworkLevelBackend, LEVEL_OPTION_ORDER } from '../enums/frameworksLevels.enum';
import { EntityService } from '../entity.service';

interface GetUserExtended extends GetUserQuery {
  checked: boolean;
}

@Component({
  selector: 'cygov-add-multi-risk-framework-modal',
  templateUrl: './add-multi-risk-framework-modal.component.html',
  styleUrls: ['./add-multi-risk-framework-modal.component.scss'],
  standalone: false,
})
export class AddMultiRiskFrameworkModalComponent implements OnInit {
  @Input() vendor: GetEntityQuery = null;
  @Input() usersList = [];
  @Input() entityId = null;

  frequencyOpt = [{ name: 'Automatically initiate re-assessment', checked: true }];
  showAdvanceSettings: boolean = false;

  @Output() closeModal = new EventEmitter<any>();
  VendorRiskFrameworkEnum = null;
  riskList: any;
  filteredRiskList = [];

  levelOptions = ['Light', 'Moderate', 'High', 'Full'];
  artifactsOptions = ['Recommended', 'Off'];
  selectedLevel = this.levelOptions[0];
  selectedArtifact = this.artifactsOptions[0];
  filteredUserList: GetUserExtended[];
  allUsersList: GetUserExtended[];
  // startDate = null;
  // endDate = new Date().getTime(); // set default value of end date
  // calendarEndDate = Date.now(); // end date to show on assessment calender
  editManager = false;

  selectedStartDate: number = null;
  selectedEndDate: number = null;

  sectionsCompletion = [false, false, false, false, false];
  selectedRisk = null;

  currentUser: GetUserQuery;
  selectedUser: GetUserQuery;
  isLoading = true;
  alreadySelectedFrameworks = [];
  searchManagerText = '';
  searchFrameworkText = '';
  previousManager: GetUserQuery = null;
  isThirdParty: boolean = false;
  currentClient = null;
  frequencyDays = 365;

  constructor(
    private fileService: FileService,
    private userService: UserService,
    private toastrService: ToastrService,
    private domainFrameworkService: DomainFrameworkService,
    private router: Router,
    private entityService: EntityService
  ) {
    this.VendorRiskFrameworkEnum = this.fileService.importFrameworkEnumsFromS3(
      FrameworkEnumTypes.VENDOR_RISK_FRAMEWORK_ENUM
    );
  }

  async ngOnInit(): Promise<void> {
    this.isThirdParty = this.router.url.includes('third-party');
    // 1- Need to check for the already selected risk frameworks for this vendor.
    this.checkAlreadyPresentRiskFrameworks();
    const parentEntity = await this.entityService.getEntity(this.entityId, true);
    const frameworkSettings = parentEntity?.frameworkSettings
      ? JSON.parse(parentEntity.frameworkSettings)
      : this.domainFrameworkService.domainSettingsData;
    const domainSettings = this.domainFrameworkService.domainSettingsData;
    const vendorFrameworks = [
      ...frameworkSettings.VENDOR_FRAMEWORKS.filter(
        framework =>
          !!framework.status &&
          domainSettings.VENDOR_FRAMEWORKS.find(obj => obj.name === framework.name && obj.status === true)
      ),
    ];
    const childrenFramework = this.formatFrameworksByGroup(vendorFrameworks);
    // add level options
    const riskList = childrenFramework.map(risk => {
      if (risk.children && risk.children.length > 0) {
        // Map children's names into the level array
        const childNames = risk.children.map(child => {
          const levelName = child.name.split(' ');
          let localName = '';
          for (const [index, name] of levelName.entries()) {
            localName = index === 0 ? name : localName + ' ' + name;
            if (localName.toLowerCase() === child.frameworkFamily.toLowerCase()) {
              // Return the remaining values starting from the matched index
              return levelName.slice(index + 1).join(' ');
            }
          }
        });
        return {
          ...risk,
          alreadySelected: false,
          level: this.sortLevels(childNames),
        };
      } else {
        // Default to 'Full' if there are no children
        return {
          ...risk,
          alreadySelected: false,
          level: ['Full'],
        };
      }
    });

    // Sort the risk list by prioritization
    this.riskList = this.prioritizeRisks(riskList);

    this.levelOptions = this.riskList[0].level;
    this.checkALreadyExistFramework();

    // 3- Set the date for the new framework. Initial and collection date for the current framework will be same as vendor.
    this.setDate();

    // 4- All the API calls will be called in the IIFE.
    (async () => {
      try {
        // 5- Getting the list of all the users and filtering them.
        await this.getAllUserList();
        // Updating the local variables.
        this.populateLocalData();
        this.isLoading = false;
      } catch (e) {
        console.log('Cannot open the new multi risk framework', e);
        this.closeModal.emit(null);
      }
    })();
  }
  sortLevels(levels: string[]): string[] {
    const levelOptionsOrder = LEVEL_OPTION_ORDER;
    const orderIndex = {};
    levelOptionsOrder.forEach((opt, index) => {
      orderIndex[opt] = index;
    });

    return levels.sort((a, b) => {
      return (orderIndex[a] ?? Infinity) - (orderIndex[b] ?? Infinity);
    });
  }

  formatFrameworksByGroup(frameworks) {
    interface Framework {
      key: string;
      name: string;
      status: boolean;
      frameworkFamily?: string;
      custom?: boolean;
    }

    interface GroupedFramework {
      name: string;
      key: string;
      status: boolean;
      children?: Framework[];
      frameworkFamily?: string;
      custom?: boolean;
    }

    const groupedFrameworks: { [key: string]: GroupedFramework } = {};
    const result: GroupedFramework[] = [];

    frameworks.forEach(framework => {
      if (framework.frameworkFamily) {
        // If the framework has a frameworkFamily, group it
        if (!groupedFrameworks[framework.frameworkFamily]) {
          groupedFrameworks[framework.frameworkFamily] = {
            name: framework.frameworkFamily,
            key: framework.frameworkFamily,
            status: false, // initially set to false, will update later
            children: [],
          };
        }

        groupedFrameworks[framework.frameworkFamily].children.push({
          name: framework.name,
          key: framework.key,
          status: framework.status,
          frameworkFamily: framework.frameworkFamily,
          custom: framework.custom,
        });

        // Update parent status to true if any child has status true
        if (framework.status) {
          groupedFrameworks[framework.frameworkFamily].status = true;
        }
      } else {
        // If the framework has no frameworkFamily, add it directly to the result
        result.push({
          name: framework.name,
          key: framework.key,
          status: framework.status,
          frameworkFamily: framework.frameworkFamily,
          custom: framework.custom,
        });
      }
    });

    // Convert grouped frameworks into an array and sort children if any
    const groupedArray = Object.values(groupedFrameworks);

    // Combine the individual frameworks and grouped frameworks, and sort by name
    return result.concat(groupedArray).sort((a, b) => a.name.localeCompare(b.name));
  }

  checkALreadyExistFramework() {
    if (this.alreadySelectedFrameworks?.length) {
      this.riskList.forEach(rl => {
        this.alreadySelectedFrameworks.forEach(selectedFrameWork => {
          if (selectedFrameWork.split('#')[0] === this.domainFrameworkService.getFrameworkKey(rl.name)) {
            rl.alreadySelected = true;
          }
        });
      });
    }
  }

  /**
   * convert the array in to expected format
   */
  riskListObjectMapper() {
    this.riskList.map((r, index) => {
      const dummyObj = { name: r, alreadySelected: false };
      this.riskList[index] = { ...dummyObj };
    });
  }

  /**
   * This function update the levelOptions array according to selected risk
   * @param value name of selected framework
   */
  onFrameworkSelection(value) {
    this.selectedRisk = value;
    this.levelOptions = this.sortLevels(value.level);
  }

  // initially setting the level options according to riskList

  setLocalData() {
    this.riskList.map((r, index) => {
      if (index === 0) {
        // const mergedLevel = { ...FrameworkLevelFrontend[this.currentClient], ...FrameworkLevelFrontend.COMMON };
        this.levelOptions = this.riskList[0].level;
        this.selectedLevel = this.levelOptions[0];
        this.selectedRisk = this.riskList[0];
      }
    });
  }

  /**
   * Helper function to populate the local variables.
   * @returns => nothing; only update the local variables.
   */
  populateLocalData(): void {
    this.filteredUserList = this.allUsersList?.length ? JSON.parse(JSON.stringify(this.allUsersList)) : [];
    if (this.filteredUserList?.length) {
      // we need to see the vendor POC and then set the current selected user.
      this.filteredUserList?.forEach(user => {
        if (user.email.toLowerCase() === this.vendor?.vendorDetails?.primaryPoc?.email.toLowerCase()) {
          this.selectedUser = user;
          user.checked = true;
        }
      });
      this.previousManager = this.selectedUser;
    }
    if (this.riskList?.length) {
      this.filteredRiskList = [...this.riskList];
      for (const risk of this.riskList) {
        if (!risk.alreadySelected) {
          this.selectedRisk = risk;
          break;
        }
      }
      this.setLocalData();
    }
  }

  prioritizeRisks(riskList): any {
    const arrayMove = (arr, oldIndex, newIndex) => {
      if (newIndex >= arr.length) {
        let k = newIndex - arr.length + 1;
        while (k--) {
          arr.push(undefined);
        }
      }
      arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
      return arr; // for testing
    };

    const findIndexInList = (arr, key, value): number => {
      return arr.findIndex(element => {
        if (element[key] === value) {
          return true;
        } else if (element.hasOwnProperty('children')) {
          return findIndexInList(element.children, key, value);
        } else {
          return false;
        }
      });
    };

    let index: number;
    switch (true) {
      case UtilsService.isENBD:
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_ENBD);
        arrayMove(riskList, index, 0); // move to the top the array
        break;
      case UtilsService.isAdnoc:
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_PRESAQ);
        arrayMove(riskList, index, 0); // move to the top the array
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_PRESAQ_V2);
        arrayMove(riskList, index, 1); // move VENDOR_RISK_PRESAQ_V2 framework as second
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_SAQ);
        arrayMove(riskList, index, 2); // move VENDOR_RISK_SAQ framework as third
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_SAQ_V2);
        arrayMove(riskList, index, 3); // move VENDOR_RISK_SAQ_V2 framework as forth
        break;
      case UtilsService.isInformatica || UtilsService.isInformaticaUat:
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_INFORMATICA_1);
        arrayMove(riskList, index, 0); // move to the top the array
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_INFORMATICA_2);
        arrayMove(riskList, index, 1); // move VENDOR_RISK_INFORMATICA_2 framework as second
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_INFORMATICA_3);
        arrayMove(riskList, index, 2); // move VENDOR_RISK_INFORMATICA_3 framework as third
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_OSAE);
        arrayMove(riskList, index, 3); // move VENDOR_RISK_OSAE framework as forth
        break;
      case UtilsService.isMoelis:
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_MOELIS_M1);
        arrayMove(riskList, index, 0); // move to the top the array
        break;
      case UtilsService.isHyperclear:
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK_HQ);
        arrayMove(riskList, index, 0); // move to the top the array
        break;
      default:
        index = findIndexInList(riskList, 'key', this.VendorRiskFrameworkEnum.VENDOR_RISK);
        arrayMove(riskList, index, 0); // move to the top the array
        break;
    }
    return riskList;
  }

  /**
   * Helper function to check the already selected frameworks.
   * @returns nothing => updates the global variable of already selected frameworks.
   */
  checkAlreadyPresentRiskFrameworks(): void {
    if (this.vendor?.activeAssessment?.standardFrameworkList?.items?.length) {
      this.vendor?.activeAssessment?.standardFrameworkList.items.forEach(framework => {
        if (framework.type === StandardType.RISK_FRAMEWORK && !framework.archived) {
          this.alreadySelectedFrameworks.push(framework.key);
        }
      });
    }
  }

  /**
   * Helper function to update when next is selected.
   * @param index => of the current task happening.
   * @returns nothing => just update the boolean value of completed sections.
   */
  onSectionCompletion(index): void {
    if (index === 0 && this.isAlreadyAdded()) {
      this.toastrService.info('Framework with this level already added, please change level or add another one!');
      return;
    }
    this.sectionsCompletion[index] = true;
  }

  isAlreadyAdded(): boolean {
    const framework_key = UtilsService.getFullFrameworkKey(this.selectedRisk?.name, this.selectedLevel);
    const currentFrameworkKey = FrameworkLevelBackend[framework_key.toUpperCase()];
    this.alreadySelectedFrameworks.findIndex(item => item === currentFrameworkKey);
    return this.alreadySelectedFrameworks.findIndex(item => item === currentFrameworkKey) > -1;
  }

  /**
   * to get the list of all the vendor users of this current assessment.
   */
  async getAllUserList(): Promise<void> {
    try {
      if (!this.usersList?.length) {
        this.usersList = await this.userService.getAssignedUsersInEntity(this.vendor.id);
      }
      await this.userService.getRolesByDefaultOrEntityId(UsersSettingConstant.default, true);
      const check = this.userService.returnHelperFunction();
      this.allUsersList = [];
      this.usersList.forEach(user => {
        if (
          this.isThirdParty ||
          check(user, RoleEnum.ADMIN) ||
          check(user, RoleEnum.MSSP) ||
          (!check(user, RoleEnum.PARTICIPANT) &&
            !check(user, RoleEnum.VENDOR) &&
            (user?.entityIds?.includes(this.entityId) || user?.entityIds?.includes(this.vendor?.id)))
        ) {
          const userObj = Object.assign({ checked: false }, user);
          const userRoleObj = this.userService.getRequiredRole(user.roleId);
          userObj.role = userRoleObj?.name ? userRoleObj.name.toUpperCase() : '';
          this.allUsersList.push(userObj);
        }
      });
    } catch (e) {
      console.error('Error while fetching users list => ', e);
    }
  }

  /**
   * Updating the list of vendor user on closing the edit mode.
   */
  closeManagerSelection(): void {
    this.editManager = !this.editManager;
    this.filteredUserList.forEach(userItem => userItem?.id === this.previousManager?.id);
  }

  /**
   * Setting the date on starting the modal.
   */
  setDate(): void {
    this.selectedStartDate = new Date().getTime();

    const endDate = new Date(this.selectedStartDate);
    endDate.setMonth(endDate.getMonth() + 1);
    this.selectedEndDate = endDate.getTime();
  }

  /**
   * Method to add the framework and close the modal.
   */
  addFramework(): void {
    if (this.isAlreadyAdded()) {
      this.toastrService.info('Framework with this level already added, please change level or add another one!');
      return;
    }
    if (this.selectedEndDate < this.selectedStartDate) {
      this.toastrService.error('End date cannot be before start date');
      return;
    }
    this.closeModal.emit({
      riskFramework: this.selectedRisk.name,
      level: this.selectedLevel,
      artifact: this.selectedArtifact,
      user: this.selectedUser,
      initialDate: this.selectedStartDate,
      collectionDate: this.selectedEndDate,
      days: this.frequencyDays,
      automaticallyInitiateReassessment: this.frequencyOpt[0].checked,
    });
  }

  /**
   * Method to update the selected artifact.
   * @param artifact => selected artifact.
   */
  selectArtifact(artifact: any): void {
    this.selectedArtifact = artifact;
  }

  /**
   * Method to update the selected level.
   * @param level => selected level.
   */
  selectLevel(level: any): void {
    this.selectedLevel = level;
  }

  /**
   * Updating the selected manager.
   * @param user => updated user.
   */
  changeSelectedManager(user: GetUserExtended): void {
    this.filteredUserList.forEach(userItem => {
      if (userItem.id === user.id) {
        userItem.checked = true;
      } else {
        userItem.checked = false;
      }
    });
  }

  /**
   * Function will be called when user is selected from the list of vendors and added.
   */
  addManager(): void {
    this.editManager = false;
    this.filteredUserList.forEach(userItem => {
      if (userItem.checked) {
        this.selectedUser = userItem;
      }
    });
  }

  dateChanged(): void {
    this.toastrService.warning('Date will be same as of current vendor, it cannot be changed');
    this.setDate();
  }

  startDateChanged(date): void {
    this.selectedStartDate = new Date(date).getTime();
  }

  endDateChanged(date): void {
    this.selectedEndDate = new Date(date).getTime();
  }

  /**
   *  Helper function to search the manager from users list
   */
  applyManagerSearch(): void {
    if (!this.searchManagerText) {
      this.filteredUserList = JSON.parse(JSON.stringify(this.allUsersList));
    } else {
      this.filteredUserList = this.allUsersList.filter(user => {
        return (
          user.name.toLowerCase().includes(this.searchManagerText.toLowerCase()) ||
          user.email.toLowerCase().includes(this.searchManagerText.toLowerCase())
        );
      });
    }
  }

  /**
   * Helper function to search the risk framework.
   */
  applyFrameworkSearch(): void {
    if (!this.searchFrameworkText) {
      this.filteredRiskList = [...this.riskList];
    } else {
      this.filteredRiskList = this.riskList.filter(framework =>
        framework.name.toLowerCase().includes(this.searchFrameworkText.toLowerCase())
      );
    }
  }

  focusOutHandlerInput() {
    if (this.frequencyDays > 365) {
      this.frequencyDays = 365;
    } else {
      if (this.frequencyDays < 1) {
        this.frequencyDays = 1;
      }
    }
  }

  decrementor(): void {
    if (this.frequencyDays > 1) {
      this.frequencyDays -= 1;
    } else {
      this.frequencyDays = 1;
      this.toastrService.error('Days cannot be less than 1.');
    }
  }

  incrementor(): void {
    if (this.frequencyDays < 365) {
      this.frequencyDays += 1;
    } else {
      this.frequencyDays = 365;
      this.toastrService.error('Days cannot be more than 365');
    }
  }

  checkDaysValidation(): void {
    if (this.frequencyDays < 1) {
      this.frequencyDays = 1;
      this.toastrService.error('Days cannot be less than 1.');
    } else if (this.frequencyDays > 365) {
      this.frequencyDays = 365;
      this.toastrService.error('Days cannot be more than 365');
    }
  }
}
